import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowLeft } from "react-icons/fi";
import { useState } from "react";
import PageWrapper from "../components/ui/PageWrapper";
import SplitText from "../components/ui/SplitText";
import Galaxy from "../components/ui/Galaxy";
import { projects } from "../data/projects";

// Image path map — images live in public/images/projects/
const IMAGE_PATHS = {
  pipeline: "/images/projects/capstone/pipeline.jpg",
  gnn_eval: "/images/projects/capstone/gnn_eval.png",
  graph_var: "/images/projects/capstone/graph_var.png",
  cross_dataset: "/images/projects/capstone/cross_dataset.png",
  feynstein_image_question: "/images/projects/feynstein/image-question.png",
  feynstein_peer: "/images/projects/feynstein/peer.png",
  tpc_wires: "/images/projects/nugraph/tpc_wires.png",
  confusion_matrix: "/images/projects/nugraph/confusion_matrix.png",
  attention_viz: "/images/projects/transformers/attention_viz.png",
  layer0: "/images/projects/transformers/layer0.png",
  layer5: "/images/projects/transformers/layer5.png",
  layer10: "/images/projects/transformers/layer10.png",
  induction_55: "/images/projects/transformers/5.5.png",
  induction_51: "/images/projects/transformers/5.1.png",
  induction_31: "/images/projects/transformers/3.1.png",
};

function ProjectImage({ src, caption }) {
  const [expanded, setExpanded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imageSrc = IMAGE_PATHS[src];
  if (!imageSrc || hasError) return null;

  return (
    <>
      <motion.figure
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="my-8"
      >
        <div
          className="bg-terminalLight border border-terminalBorder rounded-xl overflow-hidden cursor-pointer hover:border-accentBlue/30 transition-colors"
          onClick={() => setExpanded(true)}
        >
          <img
            src={imageSrc}
            alt={caption}
            className="w-full h-auto"
            loading="lazy"
            onError={() => setHasError(true)}
          />
        </div>
        {caption && (
          <figcaption className="text-dimText text-sm font-mono mt-3 text-center">
            {caption}
          </figcaption>
        )}
      </motion.figure>

      {expanded && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setExpanded(false)}
        >
          <img
            src={imageSrc}
            alt={caption}
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}
    </>
  );
}

export default function ProjectDetailPage() {
  const { id } = useParams();
  const project = projects.find((p) => p.id === parseInt(id));

  if (!project || !project.detail) {
    return (
      <PageWrapper className="pt-28 pb-20 bg-terminal">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-2xl font-bold text-brightText mb-4">
            Project not found
          </h1>
          <Link
            to="/projects"
            className="text-accentBlue hover:text-neonBlue transition-colors"
          >
            Back to projects
          </Link>
        </div>
      </PageWrapper>
    );
  }

  const { detail } = project;

  return (
    <PageWrapper className="bg-terminal">
      {/* Hero banner */}
      <div className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-40">
          <Galaxy
            density={1.5}
            glowIntensity={0.15}
            saturation={0.1}
            hueShift={140}
            twinkleIntensity={0.4}
            rotationSpeed={0.05}
            speed={0.4}
            mouseInteraction={false}
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-sm text-dimText hover:text-accentBlue transition-colors mb-8"
          >
            <FiArrowLeft size={16} /> Back to projects
          </Link>

          <SplitText
            text={project.title}
            tag="h1"
            className="text-3xl md:text-5xl font-bold text-brightText mb-6"
            delay={30}
            duration={0.5}
            from={{ opacity: 0, y: 20 }}
            to={{ opacity: 1, y: 0 }}
            textAlign="left"
            threshold={0.1}
          />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-wrap gap-2 mb-6"
          >
            {project.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 text-xs font-mono font-medium rounded bg-terminal text-accentBlue border border-terminalBorder"
              >
                [{tag}]
              </span>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Stats bar */}
      {detail.stats && (
        <div className="border-y border-terminalBorder bg-terminalLight">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {detail.stats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  className="text-center"
                >
                  <div className="text-2xl md:text-3xl font-bold font-mono text-accentBlue">
                    {stat.value}
                  </div>
                  <div className="text-dimText text-xs font-mono mt-1">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Article body */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        {detail.sections.map((section) => (
          <motion.section
            key={section.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-neonBlue mb-6 font-mono">
              {"// "}
              {section.title}
            </h2>
            {section.content.split("\n\n").map((paragraph, j) => (
              <p
                key={j}
                className="text-dimText leading-relaxed mb-4 text-[15px]"
              >
                {paragraph}
              </p>
            ))}

            {/* Single figure */}
            {section.figure && (
              <ProjectImage
                src={section.figure.src}
                caption={section.figure.caption}
              />
            )}

            {/* Multiple figures */}
            {section.figures &&
              section.figures.map((fig) => (
                <ProjectImage
                  key={fig.src}
                  src={fig.src}
                  caption={fig.caption}
                />
              ))}
          </motion.section>
        ))}
      </div>
    </PageWrapper>
  );
}
